/**
 * ConnectionList
 *
 * Displays user's connected email accounts with status,
 * scan controls, and reconnect UX for expired tokens.
 */

import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Loader2,
  Mail,
  RefreshCw,
  Trash2,
  Unplug,
} from 'lucide-react';
import type { EmailScanConnection, EmailProviderType } from '@/types/emailScanning';

interface ConnectionListProps {
  connections: EmailScanConnection[];
  loading: boolean;
  loadingConnections: boolean;
  onDisconnect: (connectionId: string) => void;
  onTriggerScan: (connectionId: string) => void;
  onReconnect: (provider: EmailProviderType) => void;
}

function getStatusBadge(status: EmailScanConnection['status']) {
  switch (status) {
    case 'active':
      return (
        <Badge variant="default" className="gap-1 bg-emerald-500/10 text-emerald-600 border-emerald-500/20">
          <CheckCircle2 className="h-3 w-3" />
          Connected
        </Badge>
      );
    case 'needs_reauth':
      return (
        <Badge variant="default" className="gap-1 bg-amber-500/10 text-amber-600 border-amber-500/20">
          <AlertTriangle className="h-3 w-3" />
          Reconnect Needed
        </Badge>
      );
    case 'error':
      return (
        <Badge variant="destructive" className="gap-1">
          <AlertCircle className="h-3 w-3" />
          Error
        </Badge>
      );
    case 'paused':
      return (
        <Badge variant="secondary" className="gap-1">
          <Clock className="h-3 w-3" />
          Paused
        </Badge>
      );
    default:
      return null;
  }
}

function getProviderLabel(provider: EmailProviderType): string {
  return provider === 'microsoft' ? 'Microsoft 365' : 'Google';
}

function formatLastScan(date?: Date): string {
  if (!date) return 'Never scanned';
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDays = Math.floor(diffHr / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export const ConnectionList: React.FC<ConnectionListProps> = ({
  connections,
  loading,
  loadingConnections,
  onDisconnect,
  onTriggerScan,
  onReconnect,
}) => {
  if (loadingConnections) {
    return (
      <Card className="border-border bg-card">
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (connections.length === 0) {
    return null; // Don't show empty section
  }

  return (
    <Card className="border-border bg-card">
      <CardHeader>
        <CardTitle className="text-lg">Connected Accounts</CardTitle>
        <CardDescription>
          Your email accounts being scanned for deal-related messages
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {connections.map((conn) => (
            <div
              key={conn.id}
              className="rounded-lg border border-border p-4"
            >
              {/* Reconnect Banner */}
              {conn.status === 'needs_reauth' && (
                <div className="mb-3 rounded-md bg-amber-500/10 border border-amber-500/20 p-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                    <p className="text-sm text-amber-700 dark:text-amber-400">
                      Your connection has expired. Please reconnect to resume scanning.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-amber-500/30 text-amber-600 hover:bg-amber-500/10"
                    onClick={() => onReconnect(conn.provider)}
                    disabled={loading}
                  >
                    Reconnect
                  </Button>
                </div>
              )}

              {/* Error Banner */}
              {conn.status === 'error' && conn.errorMessage && (
                <div className="mb-3 rounded-md bg-destructive/10 border border-destructive/20 p-3">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-destructive" />
                    <p className="text-sm text-destructive">
                      {conn.errorMessage}
                    </p>
                  </div>
                </div>
              )}

              {/* Connection Info */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center">
                    <Mail className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium">{conn.emailAddress}</p>
                      {getStatusBadge(conn.status)}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="text-xs text-muted-foreground">
                        {getProviderLabel(conn.provider)}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        Last scan: {formatLastScan(conn.lastScanAt)}
                      </span>
                      {conn.stats.totalMatches > 0 && (
                        <span className="text-xs text-muted-foreground">
                          {conn.stats.totalMatches} matches found
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1">
                  {conn.status === 'active' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onTriggerScan(conn.id)}
                      disabled={loading}
                      title="Scan now"
                    >
                      {loading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-destructive hover:text-destructive"
                    onClick={() => onDisconnect(conn.id)}
                    disabled={loading}
                    title="Disconnect"
                  >
                    <Unplug className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
